<script lang="ts">
export default {
  name: 'PolicyRuleAccordion',
};
</script>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { ChevronDown } from '@lucide/vue';
import AppButton from '@/components/v2/AppButton.vue';
import { formatCurrency } from '@/utilities/Money';
import { policyService } from '@/services/api';
import type { PolicyBinderAIStatusBinder } from '@/services/api';
import type { RuleViolatingPolicy } from '@/services/v2/ClientReadinessService';

const props = defineProps<{
  policy: RuleViolatingPolicy;
  isOpen: boolean;
}>();

const emit = defineEmits<{
  toggle: [];
  edit: [policyId: number, mainPolicyNumber: string];
}>();

const violatedRules = computed(() => {
  const p = props.policy;
  const rules: string[] = [];
  if (p.noLimitRule) rules.push('Policies Without a Limit');
  if (p.invalidRetentionRule) rules.push('Primary Policies Without a Retention');
  if (p.invalidAttachmentPointRule) rules.push('Excess Policies Without an Attachment Point');
  return rules;
});

function formatDate(value: string | null | undefined): string {
  return value ? value.slice(0, 10) : '—';
}

const binders = ref<PolicyBinderAIStatusBinder[]>([]);

watch(
  () => props.isOpen,
  async (open) => {
    if (!open) return;
    try {
      const resp = await policyService.binderAIStatus(props.policy.policyId);
      binders.value = resp.binders;
    } catch {
      binders.value = [];
    }
  },
  { immediate: true },
);
</script>

<template>
  <div class="border-b border-base-300 last:border-b-0">
    <button
      class="flex w-full items-center gap-3 px-4 py-3 text-left transition-colors hover:bg-base-200/50"
      :class="{ 'bg-base-200/50': isOpen }"
      @click="emit('toggle')">
      <div class="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-error/15 text-xs font-bold text-error">
        {{ violatedRules.length }}
      </div>
      <div class="flex-1">
        <div class="text-sm font-semibold">{{ policy.mainPolicyNumber || '<blank>' }}</div>
        <div class="text-xs text-base-content/60">{{ policy.coverageType }} — {{ policy.description }}</div>
      </div>
      <span class="badge badge-soft badge-sm badge-error">
        {{ violatedRules.length }} issue{{ violatedRules.length === 1 ? '' : 's' }}
      </span>
      <ChevronDown
        :size="16"
        class="text-base-content/40 transition-transform"
        :class="{ 'rotate-180': isOpen }" />
    </button>

    <div
      v-if="isOpen"
      class="border-t border-base-300 bg-base-200 px-4 py-4">
      <dl class="mb-3 grid grid-cols-2 gap-x-4 gap-y-1 text-xs sm:grid-cols-5">
        <div>
          <dt class="text-base-content/50">Policy number</dt>
          <dd class="font-mono">{{ policy.mainPolicyNumber || '—' }}</dd>
        </div>
        <div>
          <dt class="text-base-content/50">Coverage type</dt>
          <dd>{{ policy.coverageType || '—' }}</dd>
        </div>
        <div>
          <dt class="text-base-content/50">Effective date</dt>
          <dd>{{ formatDate(policy.effectiveDate) }}</dd>
        </div>
        <div>
          <dt class="text-base-content/50">Expiration date</dt>
          <dd>{{ formatDate(policy.expirationDate) }}</dd>
        </div>
        <div>
          <dt class="text-base-content/50">Premium</dt>
          <dd>{{ formatCurrency(policy.policyPremium ?? 0) }}</dd>
        </div>
      </dl>

      <div class="mb-3">
        <div class="mb-1 text-xs font-semibold text-base-content/70">Rule violations</div>
        <ul class="ml-5 list-disc text-sm">
          <li
            v-for="r in violatedRules"
            :key="r">
            {{ r }}
          </li>
        </ul>
      </div>

      <div class="flex items-center gap-4">
        <AppButton
          primary
          data-testid="edit-policy"
          @click="emit('edit', policy.policyId, policy.mainPolicyNumber)">
          Edit Policy
        </AppButton>
        <router-link
          v-if="binders.length >= 1"
          :to="{ path: '/ai-results', query: { resultId: binders[0].aiPromptResultID } }"
          class="text-primary hover:underline"
          data-testid="review-ai-results">
          Review AI Results
        </router-link>
      </div>
    </div>
  </div>
</template>
